import type { Canvas } from 'fabric';

import type {
  DugoutFormationObject,
  DugoutFormationScene,
} from '@/config/dugoutFormations';
import type { IconRef, ItemType } from '@/config/itemTypes';
import addItem from '@/utils/addItem';
import addShirt from '@/utils/addShirt';

// Dugout ships each formation as a full baked Fabric-2.3.x drawing that its `PresetLoader` imports
// wholesale. We keep the positions as typed DATA (`config/dugoutFormations.ts`) and re-place them
// through the tool's own `addShirt`/`addItem` instead — so a formation is created exactly like a
// hand-placed drawing (same recolour, sourcePath, export shape), with no baked 2.3.x JSON to carry.

// The item pools a formation object can resolve against.
export interface ItemCatalog {
  shirtTypes: ItemType[];
  goalTypes: ItemType[];
  itemTypes: ItemType[];
  coneTypes: ItemType[];
}

// `field-text` is Dugout's rotated, empty field-label placeholder — not a placeable item (importJSON
// skips it too).
const SKIP_VARIATIONS = new Set(['field-text']);

// Dugout pitches render at scale 1 (the 615 PNG fills the 615 canvas — see setField's dugout branch).
const DUGOUT_SCALE = 1;

// Resolve a formation object to its ItemType. `shirt-attack`/`shirt-defend` intentionally repeat
// across teams (jouw team / extra team), so match on variation AND colour first, then fall back to
// variation alone. Exported for the data-integrity test (every formation object must be placeable).
export const resolveItemType = (
  obj: DugoutFormationObject,
  catalog: ItemCatalog,
): ItemType | undefined => {
  const pools = [
    catalog.shirtTypes,
    catalog.goalTypes,
    catalog.itemTypes,
    catalog.coneTypes,
  ];
  for (const pool of pools) {
    const match = pool.find(
      (i) =>
        i.variation === obj.variation &&
        (obj.color == null || i.color === obj.color),
    );
    if (match) return match;
  }
  for (const pool of pools) {
    const match = pool.find((i) => i.variation === obj.variation);
    if (match) return match;
  }
  return undefined;
};

// Pick the field for a NEW empty (no-formation) dugout drawing. Dugout's POC always uses field-01 for
// a non-futsal empty drawing (it ignores voetbalvorm); we honour voetbalvorm per the documented
// mapping (8→field-02, 6→field-03) — strictly more correct and dugout-only.
export const pickDugoutEmptyField = (
  pitchType: string | null,
  voetbalvorm: string | null,
  fieldTypes: ItemType[],
): ItemType | null => {
  const byVariation = (v: string) =>
    fieldTypes.find((f) => f.variation === v) ?? null;
  if (pitchType === 'futsal' || voetbalvorm === '5')
    return byVariation('field-futsal');
  if (voetbalvorm === '8') return byVariation('field-02');
  if (voetbalvorm === '6') return byVariation('field-03');
  return byVariation('field-01');
};

// Place every object of a formation scene on the canvas. The field itself is set by the caller
// (setFieldSelected) — this only adds the players/goals/ball/trainer.
const placeFormation = (
  canvas: Canvas,
  scene: DugoutFormationScene,
  catalog: ItemCatalog,
  theme: string,
): void => {
  const { iconType } = scene;

  for (const obj of scene.objects) {
    if (SKIP_VARIATIONS.has(obj.variation)) continue;

    const item = resolveItemType(obj, catalog);
    if (item?.width == null || item.height == null) {
      console.error(
        `placeFormation: no placeable item type for variation "${obj.variation}"`,
      );
      continue;
    }

    const width = item.width;
    const height = item.height;
    // Dugout preset coords are the object's TOP-LEFT corner at scale 1 (its groups use the Fabric
    // default top-left origin); rinus addShirt/addItem anchor by CENTRE, so convert.
    const left = obj.left + width / 2;
    const top = obj.top + height / 2;
    const src =
      (item[iconType as keyof ItemType] as IconRef | undefined)?.src ??
      item.defaultIcon.src;
    const color = obj.color ?? item.color;

    const shared = {
      name: item.name ?? obj.variation,
      src,
      left,
      top,
      variation: obj.variation,
      canvas,
      width,
      height,
      // stamped on the group for export — string form, matching Dugout's serialized objects
      defaultIcon: item.defaultIcon.src,
      alternateIcon: item.alternateIcon?.src,
      color,
      iconType,
      scaleFactor: DUGOUT_SCALE,
      theme,
    };

    if (obj.variation.includes('shirt')) {
      addShirt({
        ...shared,
        fontColor: item.fontColor,
        fontTop: item.fontTop,
        fontLeft: item.fontLeft,
        text: obj.text ?? '',
      });
    } else {
      addItem(shared);
    }
  }

  canvas.requestRenderAll();
};

export default placeFormation;
